import { request2, baseURL2 } from '@/utils/httpClient'
import { getAuthHeaders } from './requestHelpers'

export const createAiDesignProject = (payload) => {
  return request2({
    url: '/ai-design-project',
    method: 'POST',
    headers: getAuthHeaders(),
    data: payload,
  })
}

export const fetchAiDesignProjectList = (params = {}) => {
  return request2({
    url: '/ai-design-project/list',
    method: 'GET',
    headers: getAuthHeaders(),
    params: {
      pageNum: params.pageNum,
      pageSize: params.pageSize,
      projectType: params.projectType,
      content: params.content,
    },
  })
}

export const fetchAiDesignProject = (projectKey) => {
  return request2({
    url: `/ai-design-project/${projectKey}`,
    method: 'GET',
    headers: getAuthHeaders(),
  })
}

export const deleteAiDesignProject = (projectKey) => {
  return request2({
    url: `/ai-design-project/${projectKey}`,
    method: 'DELETE',
    headers: getAuthHeaders(),
  })
}

export const saveAiDesignProjectEditorJson = (projectKey, data) => {
  return request2({
    url: `/ai-design-project/${projectKey}/floorplan-design/editor-json`,
    method: 'POST',
    headers: getAuthHeaders(),
    data,
  })
}

export const recommendArchitectureProducts = (payload) => {
  return request2({
    url: '/floorplan-design/products/recommend',
    method: 'POST',
    headers: getAuthHeaders(),
    data: payload,
  })
}

export const fetchFloorplanProducts = (params) => {
  return request2({
    url: '/floorplan-design/products',
    method: 'GET',
    headers: getAuthHeaders(),
    params,
  })
}

/** 用户侧：上架设计素材（编辑器家具库等）；可传 keyword/category/room/style 筛选 */
export const fetchDesignAssets = (params) => {
  return request2({
    url: '/design-assets/list',
    method: 'GET',
    headers: getAuthHeaders(),
    params: normalizeDesignAssetListParams(params),
  })
}

function normalizeDesignAssetListParams(params) {
  if (!params || typeof params !== 'object') return undefined
  const next = {}
  ;['keyword', 'category', 'room', 'style'].forEach((key) => {
    const value = params[key]
    if (value == null) return
    const trimmed = String(value).trim()
    if (trimmed) next[key] = trimmed
  })
  return Object.keys(next).length ? next : undefined
}

export const createAiDesignInfo = (data) => {
  return request2({
    url: '/ai-design-info',
    method: 'POST',
    headers: getAuthHeaders(),
    data,
  })
}

export const fetchAiDesignInfo = (designIdempotencyKey) => {
  return request2({
    url: `/ai-design-info/${designIdempotencyKey}`,
    method: 'GET',
    headers: getAuthHeaders(),
  })
}

export const fetchMechanicsByStructure = (params) => request2({
  url: '/ai-design-info/mechanics-by-structure',
  method: 'GET',
  headers: getAuthHeaders(),
  params,
})

export const fetchLatestAiDesignInfo = (params) => {
  return request2({
    url: '/ai-design-info/latest-by-project',
    method: 'GET',
    headers: getAuthHeaders(),
    params,
  })
}

export const saveAiDesignEditorJson = (params = {}) => {
  const form = new FormData()
  form.append('projectSource', params.projectSource || 'MAKE_HOUSE')
  form.append('idempotencyKey', params.idempotencyKey || '')
  form.append('algorithm', params.algorithm || 'DRAWING_VECTORIZE')
  const editorJson = params.editorJson
  const part = editorJson instanceof Blob
    ? editorJson
    : new Blob([typeof editorJson === 'string' ? editorJson : JSON.stringify(editorJson)], { type: 'application/json' })
  form.append('editorJson', part, 'editor.json')
  return request2({
    url: '/ai-design-info/drawing-vectorize/editor-json',
    method: 'POST',
    headers: getAuthHeaders(),
    data: form,
  })
}

export const estimateDrawingVectorize = (data) => {
  return request2({
    url: '/drawing-vectorize/estimate',
    method: 'POST',
    headers: getAuthHeaders(),
    data,
  })
}

export const calibrateDrawingVectorize = (payload) => {
  return request2({
    url: '/drawing-vectorize/calibrate',
    method: 'POST',
    headers: getAuthHeaders(),
    data: payload,
  })
}

export const submitAiDesignFeedback = (payload) => {
  return request2({
    url: '/ai-design-info/feedback',
    method: 'POST',
    headers: getAuthHeaders(),
    data: payload,
  })
}

export function toAiDesignFilePreviewUrl(fileName, opts = {}) {
  const key = readAiDesignObjectKey(fileName)
  if (!key) return null
  let url = `${normalizeBackendBaseURL(baseURL2)}/ai-design-info/files/preview?fileName=${encodeURIComponent(key)}`
  if (opts.w != null && opts.w !== '') url += `&w=${encodeURIComponent(opts.w)}`
  if (opts.q != null && opts.q !== '') url += `&q=${encodeURIComponent(opts.q)}`
  return url
}

export const previewAiDesignFile = (fileName, opts = {}) => {
  const params = { fileName }
  if (opts.w != null && opts.w !== '') params.w = opts.w
  if (opts.q != null && opts.q !== '') params.q = opts.q
  // 预览接口回 public, max-age=86400。固定路径原地覆盖的文档（编辑器 editor.json）
  // 从 HTTP 缓存里拿会读到旧版本，调用方可要求绕过缓存：换个 URL 就不命中。
  if (opts.noCache) params._ts = Date.now()
  return request2({
    url: '/ai-design-info/files/preview',
    method: 'GET',
    headers: getAuthHeaders(),
    params,
    responseType: opts.responseType || 'text',
  })
}

export const downloadAiDesignFile = (fileName) => {
  return request2({
    url: '/ai-design-info/files/download',
    method: 'GET',
    headers: getAuthHeaders(),
    params: { fileName },
    responseType: 'blob',
  })
}

function normalizeBackendBaseURL(baseURL) {
  return String(baseURL || '').replace(/\/+$/, '')
}

function readAiDesignObjectKey(fileName) {
  if (typeof fileName !== 'string') return ''
  const key = fileName.trim()
  if (!key) return ''
  if (/^https?:\/\//i.test(key)) return ''
  if (key.charAt(0) === '/') return ''
  if (key.indexOf('..') !== -1) return ''
  if (key.indexOf('\\') !== -1) return ''
  return key
}
